import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Loader2, RefreshCw, ShieldCheck, Globe, Settings2, KeyRound, Pencil } from "lucide-react";

const API = getApiUrl();

const STATUS_STYLES = {
  verified: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  temporary_failure: "bg-red-100 text-red-800",
  not_started: "bg-gray-100 text-gray-700",
};

export default function ResendDomainsTab() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [apiKeyConfig, setApiKeyConfig] = useState(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [savingKey, setSavingKey] = useState(false);

  const loadConfig = async () => {
    try {
      const r = await axios.get(`${API}/admin/resend/config`, { withCredentials: true });
      setApiKeyConfig(r.data);
    } catch (e) {
      // non-fatal — domains list load will surface auth errors if any
    }
  };

  const handleSaveApiKey = async () => {
    if (!newApiKey.trim()) {
      toast.error("Enter an API key");
      return;
    }
    setSavingKey(true);
    try {
      await axios.put(`${API}/admin/resend/config`, { api_key: newApiKey.trim() }, { withCredentials: true });
      toast.success("Resend API key updated");
      setShowKeyDialog(false);
      setNewApiKey("");
      loadConfig();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save API key");
    } finally {
      setSavingKey(false);
    }
  };

  const handleResetApiKey = async () => {
    try {
      await axios.delete(`${API}/admin/resend/config`, { withCredentials: true });
      toast.success("Reverted to the server's default API key");
      loadConfig();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset API key");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/resend/domains`, { withCredentials: true });
      setDomains(r.data.domains || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load domains");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadConfig(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error("Enter a domain name, e.g. example.com");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/resend/domains`, { name: newName.trim() }, { withCredentials: true });
      toast.success(`Domain ${newName.trim()} added — add the DNS records to verify it`);
      setShowCreate(false);
      setNewName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add domain");
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (domainId) => {
    setDetail({ id: domainId });
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/admin/resend/domains/${domainId}`, { withCredentials: true });
      setDetail(r.data.domain);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load domain");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleVerify = async (domainId) => {
    setBusyId(domainId);
    try {
      await axios.post(`${API}/admin/resend/domains/${domainId}/verify`, {}, { withCredentials: true });
      toast.success("Verification check triggered — status updates once DNS propagates");
      load();
      if (detail?.id === domainId) openDetail(domainId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleTracking = async (domainId, field, value) => {
    try {
      await axios.put(`${API}/admin/resend/domains/${domainId}`, { [field]: value }, { withCredentials: true });
      toast.success("Domain settings updated");
      if (detail?.id === domainId) setDetail((d) => ({ ...d, [field]: value }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const handleDelete = async (domainId) => {
    setBusyId(domainId);
    try {
      await axios.delete(`${API}/admin/resend/domains/${domainId}`, { withCredentials: true });
      toast.success("Domain removed");
      setDomains((prev) => prev.filter((d) => d.id !== domainId));
      if (detail?.id === domainId) setDetail(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="resend-domains-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Globe size={18} /> Resend</h3>
          <p className="text-sm text-muted-foreground">
            Add and verify sending domains so your automated emails deliver from your own address.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="resend-domains-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="resend-domains-add-btn">
            <Plus size={14} className="mr-1" /> Add Domain
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="flex items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <KeyRound size={18} className="text-muted-foreground" />
            <div>
              <div className="text-sm font-medium">API Key</div>
              <p className="text-xs text-muted-foreground">
                {apiKeyConfig?.has_key ? (
                  <>
                    <code className="bg-muted/60 px-1 rounded">{apiKeyConfig.masked_key}</code>
                    {" — "}{apiKeyConfig.source === "custom" ? "custom key saved via this dashboard" : "using server default key"}
                  </>
                ) : "No API key configured"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {apiKeyConfig?.source === "custom" && (
              <Button variant="outline" size="sm" onClick={handleResetApiKey} data-testid="resend-reset-key-btn">
                Reset to default
              </Button>
            )}
            <Button size="sm" onClick={() => setShowKeyDialog(true)} data-testid="resend-manage-key-btn">
              <Pencil size={14} className="mr-1" /> Manage Key
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
          ) : domains.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              No domains added yet. Click "Add Domain" to connect a sending domain.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Domain</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((d) => (
                  <TableRow key={d.id} data-testid={`resend-domain-row-${d.id}`}>
                    <TableCell className="font-medium">
                      <button className="hover:underline" onClick={() => openDetail(d.id)} data-testid={`resend-domain-name-${d.id}`}>
                        {d.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{d.region}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[d.status] || STATUS_STYLES.not_started}`}>
                        {d.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleVerify(d.id)}
                          disabled={busyId === d.id}
                          data-testid={`resend-domain-verify-${d.id}`}
                          title="Verify domain"
                        >
                          {busyId === d.id ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openDetail(d.id)} title="Settings"
                          data-testid={`resend-domain-settings-${d.id}`}>
                          <Settings2 size={14} />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive" title="Delete"
                              data-testid={`resend-domain-delete-${d.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove domain "{d.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Emails using this sending domain will fall back to the default sender. This can't be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => handleDelete(d.id)}
                                data-testid={`resend-domain-delete-confirm-${d.id}`}
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Domain Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-[460px]" data-testid="resend-add-domain-dialog">
          <DialogHeader>
            <DialogTitle>Add Sending Domain</DialogTitle>
            <DialogDescription>
              Enter the domain you own (e.g. example.com). You'll get DNS records to add afterwards.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Domain name</Label>
            <Input placeholder="example.com" value={newName} onChange={(e) => setNewName(e.target.value)}
              data-testid="resend-add-domain-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="resend-add-domain-confirm-btn">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain Detail / Settings Dialog */}
      <Dialog open={!!detail} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="resend-domain-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.name || "Domain"}</DialogTitle>
            <DialogDescription>DNS records and sending settings for this domain.</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" /></div>
          ) : detail && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[detail.status] || STATUS_STYLES.not_started}`}>
                  {detail.status}
                </span>
                <Button variant="outline" size="sm" onClick={() => handleVerify(detail.id)} data-testid="resend-detail-verify-btn">
                  <ShieldCheck size={14} className="mr-1" /> Verify Now
                </Button>
              </div>

              {Array.isArray(detail.records) && detail.records.length > 0 && (
                <div className="border rounded-md overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-2">Type</th>
                        <th className="p-2">Name</th>
                        <th className="p-2">Value</th>
                        <th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.records.map((r, i) => (
                        <tr key={i} className="border-t" data-testid={`resend-dns-record-${i}`}>
                          <td className="p-2 font-mono">{r.type}</td>
                          <td className="p-2 font-mono break-all">{r.name}</td>
                          <td className="p-2 font-mono break-all">{r.value}</td>
                          <td className="p-2">{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Click tracking</Label>
                  <Switch
                    checked={!!detail.click_tracking}
                    onCheckedChange={(v) => handleToggleTracking(detail.id, "click_tracking", v)}
                    data-testid="resend-detail-click-tracking-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Open tracking</Label>
                  <Switch
                    checked={!!detail.open_tracking}
                    onCheckedChange={(v) => handleToggleTracking(detail.id, "open_tracking", v)}
                    data-testid="resend-detail-open-tracking-switch"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">TLS</Label>
                  <Select
                    value={detail.tls || "opportunistic"}
                    onValueChange={(v) => handleToggleTracking(detail.id, "tls", v)}
                  >
                    <SelectTrigger className="w-40 h-8" data-testid="resend-detail-tls-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="opportunistic">Opportunistic</SelectItem>
                      <SelectItem value="enforced">Enforced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage API Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="sm:max-w-[460px]" data-testid="resend-manage-key-dialog">
          <DialogHeader>
            <DialogTitle>Manage Resend API Key</DialogTitle>
            <DialogDescription>
              Paste a new key from your Resend dashboard (Settings → API Keys). It takes effect immediately
              across all automated emails and domain actions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>API key</Label>
            <Input
              type="password"
              placeholder="re_xxxxxxxxxxxxxxxxxxxxx"
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              data-testid="resend-manage-key-input"
            />
            {apiKeyConfig?.has_key && (
              <p className="text-xs text-muted-foreground">
                Current: <code className="bg-muted/60 px-1 rounded">{apiKeyConfig.masked_key}</code>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKeyDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveApiKey} disabled={savingKey} data-testid="resend-manage-key-save-btn">
              {savingKey && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
